import React, { useState, useMemo, useRef, useEffect, lazy, Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ContractTypes from './ContractTypes/ContractTypes';
import Wallet from './Wallet/Wallet';
import Webhooks from './Webhooks/Webhooks';
import Security from './Security/Security';
import Processes from './Processes/Processes';
import TenantConfiguration from './Tenant/Tenant';
import General from './General/General';
import DelegationRules from './DelegationRules/DelegationRules';
import Integrations from './Integrations/Integrations';
import { useMutation, useQueryClient } from 'react-query';
import { fetcher } from 'api/client';
import { updateProfile } from 'api/actions/profile';
import { toast } from 'components/Shared/Toast';
import { useHistory, useLocation } from 'react-router-dom';

import { useProfileState, useProfileAbility } from 'context/profile';
import Dropzone from 'react-dropzone';
import { uploadFileV2 } from 'api/actions/file';
import {
    Progress, Col, Row, Card,
    Nav,
    Container,
    NavItem,
    NavLink,
    Button,
} from 'reactstrap';
import SignatureCanvas from 'react-signature-canvas';
import { CONTROL_MODAL, ProfileViewProvider, useProfileViewDispatch } from 'context/profile-view';
import TratoLoader from 'components/Shared/TratoLoader';
import useQueryParams from 'hooks/useQueryParams';
import clsx from 'clsx';

const Signature = lazy(() => import('components/Modals/Signature'));
const BuyContracts = lazy(() => import('components/BuyContracts/BuyContracts'));
const Withdraw = lazy(() => import('components/Modals/Profile/Withdraw'));
const CreateWebhook = lazy(() => import('components/Modals/Profile/CreateWebhook'));
const PrivateKey = lazy(() => import('components/Modals/Profile/PrivateKey'));
const SignMobile = lazy(() => import('components/Modals/Profile/SignMobile'));

function ProfileContent() {
    const { t } = useTranslation();
    const history = useHistory();
    const profileAbility = useProfileAbility()
    const queryParams = useQueryParams();

    const initialMenu = queryParams.get('menu')

    const tabs = useMemo(() => [
        { name: 'profile-users', shouldShow: () => true },
        { name: 'profile-security', shouldShow: () => true },
        { name: 'processes', shouldShow: () => profileAbility.can('read', 'Profile-Processes') },
        { name: 'wallet', shouldShow: () => profileAbility.can('read', 'Own-Wallet') },
        { name: 'tenant', shouldShow: () => profileAbility.can('read', 'Admin-Profile') },
        { name: 'contract-types', shouldShow: () => profileAbility.can('read', 'Admin-Profile') },
        { name: 'webhooks', shouldShow: () => profileAbility.can('read', 'Admin-Profile') },
        { name: 'delegation-rules', shouldShow: () => profileAbility.can('read', 'Admin-Profile') },
        { name: 'integrations', shouldShow: () => profileAbility.can('read', 'Admin-Profile') },
        // 'admin',
        // 'clients',
        // 'theme',
    ], [profileAbility])

    const [active, setActive] = useState(initialMenu);

    const handleClick = useCallback((selected) => {
        history.push('/profile?menu=' + selected)
        setActive(selected)
    }, [])

    useEffect(() => {
        if (!active) handleClick(tabs[0].name)
    }, [active, tabs, handleClick])


    return (
        <Card className="rounded-lg card-shadow p-4">
            <Nav tabs fill>
                {tabs.filter((t) => t.shouldShow()).map((tab, index) =>
                    <NavItem key={`navitem-${index}`}>
                        <NavLink
                            key={`profile-tab-${index}`}
                            className={clsx({ active: tab.name === active })}
                            onClick={() => handleClick(tab.name)}>
                            {t('profile.tabs.' + tab.name)}
                        </NavLink>
                    </NavItem>
                )}
            </Nav>
            <Container fluid className="p-4">
                {{
                    'profile-users': <General />,
                    'contract-types': <ContractTypes />,
                    'processes': <Processes />,
                    'wallet': <Wallet />,
                    'profile-security': <Security />,
                    'tenant': <TenantConfiguration />,
                    'delegation-rules': <DelegationRules />,
                    'webhooks': <Webhooks />,
                    'integrations': <Integrations />,
                }[active] || <General />}
            </Container>
        </Card>
    )
}

function ProfileSideBar() {
    const { t } = useTranslation();
    const signature = useRef();
    const dispatch = useProfileViewDispatch();
    const queryClient = useQueryClient();
    const { profile } = useProfileState()

    const { givenName, familyName, email, tenant: { limits } = {}, office, photo, signatureCoords } = profile;

    const { mutate: profileMutation } = useMutation((payload) => fetcher(updateProfile(payload)), {
        onSuccess: () => {
            queryClient.invalidateQueries('profile');
            toast.success(t('success'));
        }
    });

    const handleOpenStore = () => {
        dispatch({ type: CONTROL_MODAL, open: 'STORE' })
    }
    const handleOpenSignature = () => {
        dispatch({ type: CONTROL_MODAL, open: 'SIGNATURE' })
    }

    const onDrop = async (files) => {
        const result = await fetcher(uploadFileV2(files[0]))
        profileMutation({ photo: result.url })
    }

    useEffect(() => {
        signature.current.off();
        signature.current.fromData(JSON.parse(signatureCoords?.coords || '[]'))
    }, [signatureCoords]);

    const calcTotal = (used, available) =>
        available * 100 / (used + available);

    const getColor = (used, available) => {
        const ratio = available / used;
        if (ratio >= .8) return 'info'
        if (ratio < .8 && ratio > .3) return 'warning'
        if (ratio <= .3) return 'danger'
    }

    return (
        <>
            <Card className="text-center p-4 rounded-lg card-shadow">
                <Dropzone accept='image/*' onDrop={(files) => onDrop(files)} multiple={false}>
                    {({ getRootProps, getInputProps }) => (
                        <div {...getRootProps()}>
                            <input {...getInputProps()} />
                            <div
                                className="avatar-img pointer p-2"
                            >
                                <img src={photo || '/images/buho-avatar.png'} alt="profile-pic" className="mx-auto profile-img-border mb-2" ></img>
                            </div>
                        </div>
                    )}
                </Dropzone>

                <h5 className="font-weight-bold mb-0">{givenName} {familyName}</h5>
                <span>{email}</span>
            </Card>
            <Card className="mt-3 p-4 rounded-lg card-shadow">

                <span className="text-uppercase font-weight-bold">{t('profile.plan.title')}</span>
                <h5 className="font-weight-bold p-3 text-center">{profile?.tenant?.plan}</h5>

                <span>{t('profile.plan.shield1_remaining')} {limits?.availableS1}/{limits?.availableS1 + limits?.usedS1}</span>
                <div className="mb-2">
                    <Progress striped color={getColor(limits?.usedS1, limits?.availableS1)} animated value={calcTotal(limits?.usedS1, limits?.availableS1)} />
                </div>

                <span>{t('profile.plan.shield2_remaining')} {limits?.availableS2}/{limits?.availableS2 + limits?.usedS2}</span>
                <div className="mb-2">
                    <Progress striped color={getColor(limits?.usedS2, limits?.availableS2)} animated value={calcTotal(limits?.usedS2, limits?.availableS2)} />
                </div>

                <span>{t('profile.plan.shield3_remaining')} {limits?.availableS3}/{limits?.availableS3 + limits?.usedS3}</span>
                <div className="mb-2">
                    <Progress striped color={getColor(limits?.usedS3, limits?.availableS3)} animated value={calcTotal(limits?.usedS3, limits?.availableS3)} />
                </div>

                <span>{t('profile.plan.shield4_remaining')} {limits?.availableS4}/{limits?.availableS4 + limits?.usedS4}</span>
                <div className="mb-2">
                    <Progress striped color={getColor(limits?.usedS4, limits?.availableS4)} animated value={calcTotal(limits?.usedS4, limits?.availableS4)} />
                </div>

                <span className="">{t('profile.plan.shield5_remaining')} {limits?.availableS5}/{limits?.availableS5 + limits?.usedS5}</span>
                <div className="mb-2">
                    <Progress striped color={getColor(limits?.usedS5, limits?.availableS5)} animated value={calcTotal(limits?.usedS5, limits?.availableS5)} />
                </div>

                <span className="">{t('profile.plan.fielSignature_remaining')} {limits?.availableFiels}/{limits?.availableFiels + limits?.usedFiels}</span>
                <div className="mb-2">
                    <Progress striped color={getColor(limits?.usedFiels, limits?.availableFiels)} animated value={calcTotal(limits?.usedFiels, limits?.availableFiels)} />
                </div>

                <div className="mt-3">
                    <Button color="primary" block className="font-weight-bold" onClick={handleOpenStore}>{t('profile.buy')}</Button>
                </div>

            </Card>
            <Card className="mt-3 p-4 rounded-lg card-shadow">
                <div className="text-uppercase font-weight-bold">{t('profile.office.title')}</div>
                <img className="office-img mx-auto company-img-size" src={office?.logo || '/images/no-office-logo.png'} alt="logo" width="100%" />
                <span className="font-weight-bold mt-1">{t('profile.office.name')}</span>
                <span className="text-muted">{office?.name}</span>
                <span className="font-weight-bold mt-1">{t('profile.office.representative_placeholder')}</span>
                <span className="text-muted">{office?.representative}</span>
            </Card>

            <Card className="my-3 p-4 rounded-lg card-shadow">
                <span className="text-uppercase font-weight-bold">{t('profile.signature.title')}</span>
                <SignatureCanvas
                    ref={signature}
                    canvasProps={{ width: 300, height: 150, className: 'signature-canvas' }}
                />
                <Button size="sm" block color="white" onClick={handleOpenSignature} >
                    {t('profile.signature.change')}
                </Button>

            </Card>

        </>
    )
}

function Profile() {

    return (
        <Row className="p-3">
            <Suspense fallback={<TratoLoader className="absolute-center" />}>
                <Signature />
                <SignMobile />
                <BuyContracts />
                <Withdraw />
                <CreateWebhook />
                <PrivateKey />
            </Suspense>
            <Col md="3" className="mt-3">
                <ProfileSideBar />
            </Col>
            <Col md="9" className="mt-3">
                <ProfileContent />
            </Col>
        </Row>
    )
}

export default function ProfileView() {


    return (
        <ProfileViewProvider>
            <Profile />
        </ProfileViewProvider>
    )
};